'use client';

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
  type CSSProperties,
  type HTMLAttributes,
} from 'react';
import * as RPopover from '@radix-ui/react-popover';
import { cn } from '../../lib/cn';
import { type SColor } from '../../lib/color';
import { FLOATING_SLIDE_ANIM } from '../../lib/floating-anim';
import { usePortalContainer } from '../../lib/portal-container';
import { sanitizeInlineHtml } from '../../lib/sanitize-inline-html';
import { withDisplayName } from '../../lib/display-name';
import { SIcon, type SIconName } from '../SIcon';
import { SGhostButton } from '../SGhostButton';

export type STooltipType = 'default' | 'danger' | 'warning' | 'accent';
export type STooltipPlacement = 'top' | 'bottom' | 'left' | 'right';
export type STooltipTrigger = 'hover' | 'click' | 'none';

export interface STooltipProps {
  /** 툴팁 본문 (ReactNode). message 보다 우선 */
  content?: ReactNode;
  /** 툴팁 본문 줄 목록 (각 항목이 한 줄, HTML 문자열 허용 — innerHTML 렌더링) */
  message?: string[];
  /** 열림 상태 (제어). 지정하면 controlled — hover·click·`ref.show()/hide()` 가 이 값을 덮지 않는다 */
  open?: boolean;
  /** 열림 상태 변경 요청. 트리거·자동 닫힘·X 버튼·ref 모든 경로에서 호출된다 (controlled 여부와 무관) */
  onOpenChange?: (open: boolean) => void;
  /** 트리거 방식. `'none'` = 트리거 없음 — `open` 으로만 제어되는 상시 표시 툴팁 */
  trigger?: STooltipTrigger;
  /** 표시 방향 */
  placement?: STooltipPlacement;
  /** 앵커와의 간격(px) */
  sideOffset?: number;
  /** 배경/텍스트 색상 타입 */
  tooltipType?: STooltipType;
  /** 우측 상단 닫기 버튼. 켜면 자동 닫힘이 비활성화되고 X(또는 hide())로만 닫힘 (hover·click 공통, `trigger='none'` 에서는 무시) */
  useClose?: boolean;
  /** 비활성 — 트리거만 렌더하고 툴팁을 열지 않음 */
  disabled?: boolean;
  /** 커스텀 트리거. 없으면 아이콘 트리거를 렌더 */
  children?: ReactNode;
  /** 기본(아이콘) 트리거 설정 */
  icon?: SIconName;
  iconSize?: number;
  /** 트리거 아이콘 색상. 팔레트 키(`grey_65`, `red_95` …) 또는 임의 CSS 색상 */
  color?: SColor;
  /** 접근성 레이블 */
  ariaLabel?: string;
  className?: string;
  triggerClassName?: string;
  style?: CSSProperties;
  /** 툴팁 본문 요소에 그대로 전달 (`role`, `aria-*` 등). Radix 기본 `role="dialog"` 를 덮어쓸 수 있다 */
  contentProps?: HTMLAttributes<HTMLDivElement>;
}

/** ref로 노출되는 UX 메서드 (sd-tooltip @Method show/hide 대응) */
export interface STooltipHandle {
  /** 툴팁을 표시합니다. */
  show: () => void;
  /** 툴팁을 숨깁니다. */
  hide: () => void;
}

/** mouseleave 후 닫힘까지의 유예 (sd-tooltip startHideTimer 100ms) */
const HIDE_DELAY_MS = 100;

function useBody(content?: ReactNode, message: string[] = []): ReactNode {
  if (content != null) return content;
  if (message.length === 0) return null;
  // 원본 sd-tooltip: 각 줄을 <div>로 쌓기만 하고 별도 간격 없이 line-height(20px)로 줄 간격을 잡는다.
  // 단, 단일 줄일 때는 line-height 24 로 표시한다.
  // 각 줄은 HTML 문자열을 허용하되, sanitizeInlineHtml 로 살균 후 innerHTML 렌더 (XSS 방어).
  const singleLine = message.length === 1;
  return (
    <>
      {message.map((line, i) => (
        <div
          key={i}
          className={singleLine ? 'leading-[24px]' : undefined}
          dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(line) }}
        />
      ))}
    </>
  );
}

/**
 * STooltip — sd-tooltip 포팅.
 * hover·click 모두 Radix SPopover 기반으로, sd-tooltip 인터랙션을 재현한다:
 * 즉시 열림(hover 지연 없음) · mouseleave 100ms 유예 + 본문 hover 브릿지 ·
 * useClose 시 자동 닫힘 비활성(X/hide 로만) · 4색 타입은 --cmp-tooltip-* 토큰.
 *
 * `trigger='none'` + `open` 으로 트리거 없는 상시 표시 툴팁(인라인 검증 안내)도 만든다.
 */
const STooltipImpl = forwardRef<STooltipHandle, STooltipProps>(function STooltip(
  {
    content,
    message = [],
    open: openProp,
    onOpenChange,
    trigger = 'hover',
    placement = 'top',
    sideOffset = 8,
    tooltipType = 'default',
    useClose = false,
    disabled = false,
    children,
    icon = 'helpOutline',
    iconSize = 12,
    color = '#01BB4B',
    ariaLabel,
    className,
    triggerClassName,
    style,
    contentProps,
  },
  ref,
) {
  const portalContainer = usePortalContainer();
  // open 미지정이면 내부 state 로 동작(기존 hover·click 그대로), 지정하면 controlled
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancelHide = useCallback(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = undefined;
    }
  }, []);
  // 열림/닫힘 요청의 단일 경로 — 트리거·자동 닫힘·X 버튼·ref 가 모두 여기를 지난다.
  // controlled 면 내부 state 를 건드리지 않고 onOpenChange 만 알린다.
  const setOpenState = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const openNow = () => {
    cancelHide();
    setOpenState(true);
  };
  // useClose 면 자동 닫힘 없음(수동만). 아니면 100ms 유예 후 닫힘 (본문 hover 시 취소됨)
  const startHide = () => {
    if (useClose) return;
    cancelHide();
    hideTimer.current = setTimeout(() => setOpenState(false), HIDE_DELAY_MS);
  };

  useImperativeHandle(
    ref,
    () => ({
      show: () => {
        cancelHide();
        setOpenState(true);
      },
      hide: () => {
        cancelHide();
        setOpenState(false);
      },
    }),
    [cancelHide, setOpenState],
  );

  const isHover = trigger === 'hover';
  // 트리거 없음 — 앵커만 두고 열림은 open 이 전담. 자동 닫힘·X 버튼 모두 없다.
  const isNone = trigger === 'none';

  if (isNone && children == null && typeof console !== 'undefined') {
    console.warn(
      '[STooltip] trigger="none" 은 위치 기준이 될 children 이 필요합니다. 아이콘 트리거로 폴백합니다.',
    );
  }

  const body = useBody(content, message);
  const bg = `var(--cmp-tooltip-${tooltipType}-bg)`;
  const fg = `var(--cmp-tooltip-${tooltipType}-content)`;

  const triggerNode = children ?? (
    <span className={cn('inline-flex cursor-pointer', triggerClassName)} aria-label={ariaLabel}>
      <SIcon name={icon} size={iconSize} color={color} />
    </span>
  );

  // 트리거만 렌더 (비활성/본문 없음)
  if (disabled || body == null) return <>{triggerNode}</>;

  // className·style 은 전용 prop 과 병합하고, 나머지(role·aria-* 등)만 본문에 스프레드한다
  const {
    className: contentPropsClassName,
    style: contentPropsStyle,
    ...restContentProps
  } = contentProps ?? {};

  // 원본 sd-floating-menu 와 동일하게 fit-content + 자동 줄바꿈 없음(각 줄은 message 배열로만 분리)
  const contentClass = cn(
    'z-50 w-fit whitespace-nowrap rounded-[6px] px-[16px] py-[12px] text-[12px] leading-[20px] shadow-normal-sm',
    FLOATING_SLIDE_ANIM,
    className,
    contentPropsClassName,
  );
  // default 500, danger/warning/accent 700 (원본 타이포 기준)
  const contentStyle: CSSProperties = {
    background: bg,
    color: fg,
    fontWeight: tooltipType === 'default' ? 500 : 700,
    ...style,
    ...contentPropsStyle,
  };

  return (
    <RPopover.Root open={open} onOpenChange={setOpenState}>
      {isNone ? (
        // 트리거 없음: children 을 래퍼 없이 그대로 앵커로 쓴다 (소비자 레이아웃 보존).
        // mouse/focus 핸들러가 없으므로 useClose 없이도 자동 닫힘이 발생하지 않는다.
        <RPopover.Anchor asChild>{triggerNode}</RPopover.Anchor>
      ) : isHover ? (
        // hover: Anchor(위치 기준) + 직접 mouse 핸들러 — 클릭으로 토글되지 않도록 Trigger 미사용
        <RPopover.Anchor asChild>
          <span
            className="inline-flex"
            onMouseEnter={openNow}
            onMouseLeave={startHide}
            onFocus={openNow}
            onBlur={startHide}
          >
            {triggerNode}
          </span>
        </RPopover.Anchor>
      ) : (
        <RPopover.Trigger asChild>{triggerNode}</RPopover.Trigger>
      )}
      <RPopover.Portal container={portalContainer}>
        <RPopover.Content
          side={placement}
          sideOffset={sideOffset}
          className={contentClass}
          style={contentStyle}
          // 포커스 가로채지 않음 (sd-tooltip 은 포커스 이동 없음)
          onOpenAutoFocus={e => e.preventDefault()}
          // useClose / trigger='none': 바깥 클릭·ESC 로 닫히지 않음
          // (none 은 open 이 전담하므로 불필요한 onOpenChange(false) 도 막는다)
          onInteractOutside={e => {
            if (useClose || isNone) e.preventDefault();
          }}
          onEscapeKeyDown={e => {
            if (useClose || isNone) e.preventDefault();
          }}
          // hover 브릿지: 본문 위에 올리면 닫힘 취소, 벗어나면 유예 후 닫힘
          onMouseEnter={isHover ? cancelHide : undefined}
          onMouseLeave={isHover ? startHide : undefined}
          {...restContentProps}
        >
          <div className="flex items-start gap-[12px]">
            <div className="min-w-0 flex-1">{body}</div>
            {/* 상시 표시 안내(trigger='none')에 "닫기" 는 의미가 없다 → useClose 와 분리 */}
            {useClose && !isNone && (
              <SGhostButton
                icon="close"
                size="xs"
                intent={tooltipType === 'default' ? 'inverse' : 'default'}
                ariaLabel="닫기"
                className="shrink-0"
                onClick={() => {
                  cancelHide();
                  setOpenState(false);
                }}
              />
            )}
          </div>
          <RPopover.Arrow width={16} height={12} style={{ fill: bg }} />
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
});

export const STooltip = withDisplayName(STooltipImpl, 'STooltip');
